import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { CircleAlert, Clock3, Gauge, RefreshCw } from 'lucide-react';
import { Fragment, useCallback } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { StatTile } from '@apps/web/components/ui/stat_tile';
import { useVoiceLatency } from '@apps/web/swr_data_fetching/admin_voice_qa_queries';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type VoiceLatencyResponse = ApiResponseType<'/admin/voice-latency', 'get'>;
type ComponentKey = VoiceLatencyResponse['components'][number]['key'];

type ComponentMetadata = {
    readonly key: ComponentKey;
    readonly label: string;
    readonly shortLabel: string;
    readonly description: string;
    readonly color: string;
};

const COMPONENTS: ReadonlyArray<ComponentMetadata> = [
    {
        key: 'endpointing_ms',
        label: 'Turn finalization',
        shortLabel: 'Endpointing',
        description:
            'Detected end of caller speech to the committed turn, including speech recognition and end-of-turn detection.',
        color: 'var(--chart-6)',
    },
    {
        key: 'pipeline_dispatch_ms',
        label: 'Pipeline dispatch',
        shortLabel: 'Dispatch',
        description:
            'Caller endpoint to model request dispatch; zero when dispatch already happened at endpoint release.',
        color: 'var(--chart-1)',
    },
    {
        key: 'model_first_text_ms',
        label: 'Model first text',
        shortLabel: 'Model',
        description: 'Model request dispatch to its first text token.',
        color: 'var(--chart-2)',
    },
    {
        key: 'response_release_ms',
        label: 'Response release',
        shortLabel: 'Release',
        description:
            'First model text to caller-facing speech release, including structured-output and language guards.',
        color: 'var(--chart-3)',
    },
    {
        key: 'tts_handoff_ms',
        label: 'TTS handoff',
        shortLabel: 'Handoff',
        description: 'Speech release to the first TTS request.',
        color: 'var(--chart-4)',
    },
    {
        key: 'tts_first_audio_ms',
        label: 'TTS first audio',
        shortLabel: 'TTS audio',
        description: 'First TTS request to the first generated-reply audio.',
        color: 'var(--chart-5)',
    },
];

const COMPONENT_BY_KEY = new Map(
    COMPONENTS.map((component) => [component.key, component]),
);
const REFRESH_INTERVAL_MS = 30_000;

export const Route = createFileRoute('/_app/admin/voice-latency')({
    component: VoiceLatencyPage,
    head: () => ({
        meta: [{ title: 'Voice Latency | Phoebe Admin' }],
    }),
});

function formatLatency(value: number | null): string {
    if (value === null) {
        return '—';
    }
    if (value < 1_000) {
        return `${Math.round(value)} ms`;
    }
    return `${(value / 1_000).toFixed(2)} s`;
}

function formatHistogramRange(lowerMs: number, upperMs: number | null): string {
    if (upperMs === null) {
        return `${formatLatency(lowerMs)}+`;
    }
    if (lowerMs === 0) {
        return `<${formatLatency(upperMs)}`;
    }
    return `${formatLatency(lowerMs)}–${formatLatency(upperMs)}`;
}

type TooltipEntry = {
    readonly color?: string;
    readonly name?: string;
    readonly value?: number | string | null;
};

function LatencyTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<TooltipEntry>;
    readonly label?: string;
}) {
    if (!active || !payload?.length) {
        return null;
    }
    return (
        <div className="bg-background min-w-44 rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.name}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">
                        {formatLatency(
                            typeof entry.value === 'number'
                                ? entry.value
                                : null,
                        )}
                    </span>
                </div>
            ))}
        </div>
    );
}

function CountTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<TooltipEntry>;
    readonly label?: string;
}) {
    if (!active || !payload?.length) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="font-medium">{label}</div>
            <div className="text-muted-foreground">
                {payload[0]?.value ?? 0} measured turns
            </div>
        </div>
    );
}

function MeanTrendChart({ data }: { readonly data: VoiceLatencyResponse }) {
    const points = data.trend.map((bucket) => ({
        label: format(new Date(bucket.started_at), 'h:mm'),
        sampleCount: bucket.sample_count,
        total_ms: bucket.total_mean_ms,
        endpointing_ms: bucket.endpointing_mean_ms,
        pipeline_dispatch_ms: bucket.pipeline_dispatch_mean_ms,
        model_first_text_ms: bucket.model_first_text_mean_ms,
        response_release_ms: bucket.response_release_mean_ms,
        tts_handoff_ms: bucket.tts_handoff_mean_ms,
        tts_first_audio_ms: bucket.tts_first_audio_mean_ms,
    }));
    return (
        <Card>
            <CardHeader>
                <CardTitle>Mean contributor trend</CardTitle>
                <CardDescription>
                    Five-minute means over the rolling hour. The stacked bars
                    add to the total line.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={points}
                            margin={{ top: 8, right: 12, left: 8, bottom: 0 }}
                            accessibilityLayer={true}
                        >
                            <CartesianGrid
                                stroke="var(--border)"
                                strokeDasharray="3 3"
                            />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11 }}
                                minTickGap={18}
                            />
                            <YAxis
                                tick={{ fontSize: 11 }}
                                width={54}
                                tickFormatter={(value: number) =>
                                    formatLatency(value)
                                }
                            />
                            <RechartsTooltip content={<LatencyTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {COMPONENTS.map((component) => (
                                <Bar
                                    key={component.key}
                                    dataKey={component.key}
                                    name={component.shortLabel}
                                    stackId="ttfa"
                                    fill={component.color}
                                    isAnimationActive={false}
                                />
                            ))}
                            <Line
                                type="monotone"
                                dataKey="total_ms"
                                name="Total mean"
                                stroke="var(--foreground)"
                                strokeWidth={2}
                                dot={false}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
}

function ContributorHistograms({
    components,
}: {
    readonly components: VoiceLatencyResponse['components'];
}) {
    return (
        <section className="space-y-3">
            <div>
                <h2 className="text-lg font-semibold">
                    Contributor distributions
                </h2>
                <p className="text-muted-foreground text-sm">
                    Each turn contributes once to every chart.
                </p>
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
                {components.map((component) => {
                    const metadata = COMPONENT_BY_KEY.get(component.key);
                    const points = component.histogram.map((bucket) => ({
                        label: formatHistogramRange(
                            bucket.lower_ms,
                            bucket.upper_ms,
                        ),
                        count: bucket.count,
                    }));
                    return (
                        <Card key={component.key}>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {metadata?.label ?? component.key}
                                </CardTitle>
                                <CardDescription>
                                    {metadata?.description} Mean{' '}
                                    {formatLatency(component.stats.mean_ms)} ·
                                    p95 {formatLatency(component.stats.p95_ms)}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-56 w-full">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart
                                            data={points}
                                            margin={{
                                                top: 4,
                                                right: 8,
                                                left: 0,
                                                bottom: 0,
                                            }}
                                            accessibilityLayer={true}
                                        >
                                            <CartesianGrid
                                                stroke="var(--border)"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis
                                                dataKey="label"
                                                interval={0}
                                                angle={-20}
                                                textAnchor="end"
                                                height={48}
                                                tick={{ fontSize: 10 }}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fontSize: 11 }}
                                                width={32}
                                            />
                                            <RechartsTooltip
                                                content={<CountTooltip />}
                                            />
                                            <Bar
                                                dataKey="count"
                                                name="Turns"
                                                fill={
                                                    metadata?.color ??
                                                    'var(--chart-1)'
                                                }
                                                maxBarSize={48}
                                                isAnimationActive={false}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </section>
    );
}

function VoiceLatencyPage() {
    const { data, error, isLoading, isValidating, mutate } = useVoiceLatency({
        refreshIntervalMs: REFRESH_INTERVAL_MS,
    });
    const handleRefresh = useCallback(() => {
        void mutate();
    }, [mutate]);

    return (
        <div className="space-y-6 p-6">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Gauge className="text-primary size-6" />
                        <h1 className="text-2xl font-semibold">
                            Voice latency
                        </h1>
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm">
                        TTFA from detected end of caller speech to first reply
                        audio for live customer voice turns. Includes turn
                        finalization; only version-2 samples with a measured
                        speech boundary in the past hour are included.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {data ? (
                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                            <Clock3 className="size-3.5" />
                            Updated{' '}
                            {format(new Date(data.generated_at), 'h:mm:ss a')}
                        </span>
                    ) : null}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isValidating}
                    >
                        {isValidating ? (
                            <Spinner size="sm" />
                        ) : (
                            <RefreshCw className="size-4" />
                        )}
                        Refresh
                    </Button>
                </div>
            </header>
            <Alert>
                <Gauge />
                <AlertTitle>What this measures</AlertTitle>
                <AlertDescription>
                    From detected caller speech end to the first reply audio:
                    turn finalization + pipeline dispatch + model first text +
                    response release + TTS handoff + TTS first audio. Carrier
                    buffering and handset playback are not measured. Calls
                    appear only after they finish and persist their timeline.
                    Interrupted and failed turns without first audio are not in
                    this view.
                </AlertDescription>
            </Alert>
            {error ? (
                <Alert variant="destructive">
                    <CircleAlert />
                    <AlertTitle>Could not load voice latency</AlertTitle>
                    <AlertDescription>
                        Refresh the page or try again in a moment.
                    </AlertDescription>
                </Alert>
            ) : null}
            {isLoading && !data ? (
                <div className="text-muted-foreground flex min-h-80 items-center justify-center gap-2 text-sm">
                    <Spinner size="lg" tone="primary" />
                    Loading voice latency…
                </div>
            ) : null}
            {data?.truncated ? (
                <Alert variant="destructive">
                    <CircleAlert />
                    <AlertTitle>Window exceeded the read limit</AlertTitle>
                    <AlertDescription>
                        The charts show a partial sample. Narrower server-side
                        aggregation is needed before using these values.
                    </AlertDescription>
                </Alert>
            ) : null}
            {data && data.sample_count === 0 ? (
                <Card>
                    <CardContent className="text-muted-foreground flex min-h-64 flex-col items-center justify-center gap-2 text-center">
                        <Gauge className="size-8" />
                        <div className="text-foreground font-medium">
                            No measured turns in the past hour
                        </div>
                        <p className="max-w-lg text-sm">
                            This exact series starts when the new voice runtime
                            deploys. Finish a normal live customer call, then
                            refresh after its timeline is saved.
                        </p>
                    </CardContent>
                </Card>
            ) : null}
            {data && data.sample_count > 0 ? (
                <Fragment>
                    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                        <StatTile
                            variant="card"
                            label="Mean TTFA"
                            value={formatLatency(data.total.mean_ms)}
                            description="Rolling one-hour mean"
                        />
                        <StatTile
                            variant="card"
                            label="p50 TTFA"
                            value={formatLatency(data.total.p50_ms)}
                            description="Median measured turn"
                        />
                        <StatTile
                            variant="card"
                            label="p95 TTFA"
                            value={formatLatency(data.total.p95_ms)}
                            description="Slow-tail measured turn"
                        />
                        <StatTile
                            variant="card"
                            label="Measured turns"
                            value={data.sample_count.toLocaleString()}
                            description="Complete additive samples"
                        />
                        <StatTile
                            variant="card"
                            label="Completed calls"
                            value={data.call_count.toLocaleString()}
                            description="Calls represented by samples"
                        />
                    </section>
                    <MeanTrendChart data={data} />
                    <ContributorHistograms components={data.components} />
                </Fragment>
            ) : null}
        </div>
    );
}
